import React, { useMemo, useRef, useEffect, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { IoMdArrowBack, IoMdArrowForward } from "react-icons/io";
import GetStartedButton from "./GetStartedButton";

const columnHelper = createColumnHelper();

function AuthorCell({ author, faviconPath, website }) {
  const [faviconLoaded, setFaviconLoaded] = useState(!!faviconPath);

  return (
    <div className="flex flex-col">
      <span className="text-[10px] sm:text-xs text-gray-700 font-medium">
        {author}
      </span>
      {website && (
        <div className="flex items-center gap-1 mt-1">
          {faviconPath && faviconLoaded ? (
            <img
              src={`/${faviconPath}`}
              alt={website}
              title={website}
              className="w-[18px] h-[18px] object-contain"
              onLoad={() => setFaviconLoaded(true)}
              onError={() => setFaviconLoaded(false)}
            />
          ) : (
            <span className="text-[10px] sm:text-xs text-gray-500">{website}</span>
          )}
        </div>
      )}
    </div>
  );
}

function ReviewOverlay({ review, onClose, onNext, onPrevious, hasNext, hasPrevious, affiliateLink, companyName }) {
  if (!review) return null;

  return (
    <Dialog open={!!review} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] h-[85vh] flex flex-col p-2 sm:p-6 [&>button]:hidden">
        {/* Navigation Header */}
        <div className="flex items-center justify-between pb-4 border-b">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
          >
            <IoMdArrowBack className="w-5 h-5" />
            <span className="text-sm font-medium">Back to Reviews</span>
          </button>

          {hasNext && (
            <button
              onClick={onNext}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors cursor-pointer"
            >
              <span className="text-sm font-medium">Next Review</span>
              <IoMdArrowForward className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto py-4 min-h-0">
          <div className="space-y-4">
            {/* Rating */}
            <div className="flex items-center gap-1">
              <span className="text-yellow-500 font-semibold">{review.starRating}</span>
              <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </div>

            {/* Author */}
            <div>
              <AuthorCell
                author={review.reviewAuthor}
                faviconPath={review.faviconPath}
                website={review.reviewWebsite}
              />
            </div>

            {/* Review Text */}
            <div>
              <p className="text-sm text-gray-900 whitespace-pre-wrap">
                {review.reviewText}
              </p>
              {review.reviewDate && (
                <p className="text-xs text-gray-500 mt-3">
                  {new Date(review.reviewDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Get Started Button - Fixed at Bottom */}
        <div className="pt-4 border-t mt-auto">
          <GetStartedButton
            affiliateLink={affiliateLink}
            companyName={companyName}
            eventSource="reviews_overlay"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ReviewsTable({ reviews, onScroll, affiliateLink, companyName }) {
  const scrollRef = useRef(null);
  const hasScrolledRef = useRef(false);
  const [selectedReviewIndex, setSelectedReviewIndex] = useState(null);

  const selectedReview = selectedReviewIndex !== null ? reviews[selectedReviewIndex] : null;

  const handleNext = () => {
    if (selectedReviewIndex !== null && selectedReviewIndex < reviews.length - 1) {
      setSelectedReviewIndex(selectedReviewIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (selectedReviewIndex !== null && selectedReviewIndex > 0) {
      setSelectedReviewIndex(selectedReviewIndex - 1);
    }
  };

  const hasNext = selectedReviewIndex !== null && selectedReviewIndex < reviews.length - 1;
  const hasPrevious = selectedReviewIndex !== null && selectedReviewIndex > 0;

  // Handle scroll event with throttling
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    let scrollTimeout;
    const handleScrollEvent = () => {
      // Only track the first scroll event to avoid spam
      if (!hasScrolledRef.current) {
        hasScrolledRef.current = true;
        onScroll();
      }

      // Reset after 2 seconds of no scrolling
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        hasScrolledRef.current = false;
      }, 2000);
    };

    scrollElement.addEventListener("scroll", handleScrollEvent);
    return () => {
      scrollElement.removeEventListener("scroll", handleScrollEvent);
      clearTimeout(scrollTimeout);
    };
  }, [onScroll]);


  const columns = useMemo(
    () => [
      columnHelper.accessor("starRating", {
        header: "Rating",
        cell: (info) => (
          <div className="flex items-center gap-1">
            <span className="text-yellow-500 font-semibold">{info.getValue()}</span>
            <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </div>
        ),
        size: 56,
      }),
      columnHelper.accessor("reviewAuthor", {
        header: "Author",
        cell: (info) => {
          const row = info.row.original;
          return (
            <AuthorCell
              author={info.getValue()}
              faviconPath={row.faviconPath}
              website={row.reviewWebsite}
            />
          );
        },
        size: 200,
      }),
      columnHelper.accessor("reviewText", {
        header: "Review",
        cell: (info) => {
          const row = info.row.original;
          const reviewText = info.getValue();
          const reviewDate = row.reviewDate;

          return (
            <div>
              <p className="text-xs sm:text-sm text-gray-900 line-clamp-5">
                {reviewText}
              </p>
              {reviewDate && (
                <p className="text-[10px] sm:text-xs text-gray-500 mt-2">
                  {new Date(reviewDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </p>
              )}
              <button
                onClick={() => {
                  const index = reviews.findIndex(r => r === row);
                  setSelectedReviewIndex(index);
                }}
                className="text-[10px] sm:text-xs text-blue-600 mt-1 hover:underline cursor-pointer"
              >
                Read More
              </button>
            </div>
          );
        },
        size: 500,
      }),
    ],
    [reviews, setSelectedReviewIndex]
  );

  const table = useReactTable({
    data: reviews,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Empty state
  if (reviews.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-gray-500 text-lg">No reviews yet</p>
          <p className="text-gray-400 text-sm mt-1">Check back later for user reviews</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ReviewOverlay
        review={selectedReview}
        onClose={() => setSelectedReviewIndex(null)}
        onNext={handleNext}
        onPrevious={handlePrevious}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        affiliateLink={affiliateLink}
        companyName={companyName}
      />
      <div
        ref={scrollRef}
        className="overflow-auto h-full rounded-lg border border-gray-200"
      >
        <table className="w-full border-collapse">
          <thead className="bg-gray-50 sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-2 py-2 sm:px-4 sm:py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200"
                    style={{ width: header.column.columnDef.size }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="hover:bg-gray-50 transition-colors"
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-2 py-2 sm:px-4 sm:py-4 text-sm"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
